import { useEffect, useRef, useState } from "react";

import { API_CONFIG } from "@/config/api";
import { tokenStorage } from "@/lib/http/tokenStorage";
import { createLogger } from "@/lib/logger";

const log = createLogger("UserPresence");

const HEARTBEAT_INTERVAL_MS = 10_000;
const LOOKUP_INTERVAL_MS = 15_000;
const RECONNECT_DELAY_MS = 3_000;
const SUBSCRIPTION_ID = "mobile-replies";

export type UserPresenceConnectionState = "idle" | "connecting" | "connected" | "error";

export type UserPresenceSnapshot = {
    userId: string;
    role: string | null;
    onlineStatus: string | null;
    appState: string | null;
    latitude: number | null;
    longitude: number | null;
    lastSeenAt: string | null;
};

type UserPresenceLookupReply = {
    type?: string;
    requestId?: string;
    success?: boolean;
    message?: string;
    errorCode?: string | null;
    data?: Partial<UserPresenceSnapshot> | null;
};

type ParsedStompFrame = {
    command: string;
    headers: Record<string, string>;
    body: string;
};

const getRealtimeSocketUrl = () => {
    const explicitUrl = import.meta.env.VITE_WEBSOCKET_BASE_URL;

    if (explicitUrl) {
        return explicitUrl;
    }

    if (import.meta.env.DEV) {
        const protocol = window.location.protocol === "https:" ? "wss:" : "ws:";
        return `${protocol}//${window.location.host}/ws`;
    }

    const apiBaseUrl = import.meta.env.VITE_API_BASE_URL ?? API_CONFIG.PRODUCTION_URL;
    const resolvedApiUrl = new URL(apiBaseUrl, window.location.origin);

    resolvedApiUrl.protocol = resolvedApiUrl.protocol === "https:" ? "wss:" : "ws:";
    resolvedApiUrl.pathname = "/ws";
    resolvedApiUrl.search = "";
    resolvedApiUrl.hash = "";

    return resolvedApiUrl.toString();
};

const buildStompFrame = (
    command: string,
    headers: Record<string, string> = {},
    body = ""
) => {
    const encodedHeaders = Object.entries(headers)
        .map(([key, value]) => `${key}:${value}`)
        .join("\n");

    if (!encodedHeaders) {
        return `${command}\n\n${body}\0`;
    }

    return `${command}\n${encodedHeaders}\n\n${body}\0`;
};

const parseStompFrame = (rawFrame: string): ParsedStompFrame | null => {
    const normalizedFrame = rawFrame.replace(/^\n+/, "");

    if (!normalizedFrame.trim()) {
        return null;
    }

    const bodySeparatorIndex = normalizedFrame.indexOf("\n\n");
    const head = bodySeparatorIndex >= 0
        ? normalizedFrame.slice(0, bodySeparatorIndex)
        : normalizedFrame;
    const body = bodySeparatorIndex >= 0
        ? normalizedFrame.slice(bodySeparatorIndex + 2)
        : "";

    const lines = head.split("\n");
    const command = lines.shift()?.trim();

    if (!command) {
        return null;
    }

    const headers = lines.reduce<Record<string, string>>((acc, line) => {
        const separatorIndex = line.indexOf(":");

        if (separatorIndex === -1) {
            return acc;
        }

        const key = line.slice(0, separatorIndex);
        const value = line.slice(separatorIndex + 1);

        acc[key] = value;
        return acc;
    }, {});

    return {
        command,
        headers,
        body,
    };
};

const normalizePresenceSnapshot = (
    payload: Partial<UserPresenceSnapshot> | null | undefined
): UserPresenceSnapshot | null => {
    if (!payload?.userId) {
        return null;
    }

    return {
        userId: payload.userId,
        role: payload.role ?? null,
        onlineStatus: payload.onlineStatus ?? null,
        appState: payload.appState ?? null,
        latitude: typeof payload.latitude === "number" ? payload.latitude : null,
        longitude: typeof payload.longitude === "number" ? payload.longitude : null,
        lastSeenAt: payload.lastSeenAt ?? null,
    };
};

export const useUserPresenceLookup = (targetUserId: string | null) => {
    const [presence, setPresence] = useState<UserPresenceSnapshot | null>(null);
    const [connectionState, setConnectionState] = useState<UserPresenceConnectionState>("idle");
    const [error, setError] = useState<string | null>(null);
    const [lastUpdatedAt, setLastUpdatedAt] = useState<string | null>(null);

    const socketRef = useRef<WebSocket | null>(null);
    const bufferRef = useRef("");
    const reconnectTimeoutRef = useRef<number | null>(null);
    const heartbeatIntervalRef = useRef<number | null>(null);
    const lookupIntervalRef = useRef<number | null>(null);
    const latestTargetUserIdRef = useRef<string | null>(targetUserId);
    const latestPresenceRef = useRef<UserPresenceSnapshot | null>(null);
    const requestCounterRef = useRef(0);
    const pendingRequestIdsRef = useRef(new Set<string>());

    latestTargetUserIdRef.current = targetUserId;
    latestPresenceRef.current = presence;

    const clearTimers = () => {
        if (reconnectTimeoutRef.current !== null) {
            window.clearTimeout(reconnectTimeoutRef.current);
            reconnectTimeoutRef.current = null;
        }

        if (heartbeatIntervalRef.current !== null) {
            window.clearInterval(heartbeatIntervalRef.current);
            heartbeatIntervalRef.current = null;
        }

        if (lookupIntervalRef.current !== null) {
            window.clearInterval(lookupIntervalRef.current);
            lookupIntervalRef.current = null;
        }
    };

    const closeSocket = () => {
        const socket = socketRef.current;
        socketRef.current = null;

        if (!socket) {
            return;
        }

        if (
            socket.readyState === WebSocket.OPEN ||
            socket.readyState === WebSocket.CONNECTING
        ) {
            socket.close();
        }
    };

    const sendLookup = () => {
        const socket = socketRef.current;
        const userId = latestTargetUserIdRef.current;

        if (!socket || socket.readyState !== WebSocket.OPEN || !userId) {
            return;
        }

        const requestId = `presence-${userId}-${Date.now()}-${requestCounterRef.current++}`;
        const body = JSON.stringify({
            type: "user_presence_lookup",
            requestId,
            targetUserId: userId,
        });

        pendingRequestIdsRef.current.add(requestId);
        socket.send(
            buildStompFrame(
                "SEND",
                {
                    destination: "/app/mobile",
                    "content-type": "application/json",
                    "content-length": String(body.length),
                },
                body
            )
        );
    };

    useEffect(() => {
        if (!targetUserId) {
            setPresence(null);
            setConnectionState("idle");
            setError(null);
            setLastUpdatedAt(null);
            clearTimers();
            closeSocket();
            pendingRequestIdsRef.current.clear();
            return;
        }

        const accessToken = tokenStorage.getAccessToken();

        if (!accessToken) {
            setPresence(null);
            setConnectionState("error");
            setError("Live presence requires an authenticated admin session.");
            return;
        }

        let disposed = false;

        setPresence(null);
        setError(null);
        setLastUpdatedAt(null);
        setConnectionState("connecting");
        pendingRequestIdsRef.current.clear();

        const startHeartbeat = () => {
            if (heartbeatIntervalRef.current !== null) {
                return;
            }

            heartbeatIntervalRef.current = window.setInterval(() => {
                if (socketRef.current?.readyState === WebSocket.OPEN) {
                    socketRef.current.send("\n");
                }
            }, HEARTBEAT_INTERVAL_MS);
        };

        const startLookupInterval = () => {
            if (lookupIntervalRef.current !== null) {
                return;
            }

            lookupIntervalRef.current = window.setInterval(() => {
                sendLookup();
            }, LOOKUP_INTERVAL_MS);
        };

        const scheduleReconnect = () => {
            if (disposed || reconnectTimeoutRef.current !== null) {
                return;
            }

            reconnectTimeoutRef.current = window.setTimeout(() => {
                reconnectTimeoutRef.current = null;
                connect();
            }, RECONNECT_DELAY_MS);
        };

        const handlePresenceReply = (payload: UserPresenceLookupReply) => {
            const currentTargetUserId = latestTargetUserIdRef.current;
            const hasMatchingRequestId = payload.requestId
                ? pendingRequestIdsRef.current.has(payload.requestId)
                : false;
            const hasMatchingUserId = payload.data?.userId === currentTargetUserId;

            if (!hasMatchingRequestId && !hasMatchingUserId) {
                return;
            }

            if (payload.requestId) {
                pendingRequestIdsRef.current.delete(payload.requestId);
            }

            if (!payload.success) {
                setError(payload.message ?? "Unable to retrieve live presence.");
                return;
            }

            const snapshot = normalizePresenceSnapshot(payload.data);

            if (!snapshot) {
                setError("Live presence returned an empty payload.");
                return;
            }

            setPresence(snapshot);
            setError(null);
            setLastUpdatedAt(new Date().toISOString());
        };

        const handleFrame = (frame: ParsedStompFrame) => {
            if (frame.command === "CONNECTED") {
                setConnectionState("connected");
                setError(null);

                if (socketRef.current?.readyState === WebSocket.OPEN) {
                    socketRef.current.send(
                        buildStompFrame("SUBSCRIBE", {
                            id: SUBSCRIPTION_ID,
                            destination: "/user/queue/mobile/replies",
                        })
                    );
                }

                startHeartbeat();
                sendLookup();
                startLookupInterval();
                return;
            }

            if (frame.command === "MESSAGE") {
                try {
                    const payload = JSON.parse(frame.body.trim()) as UserPresenceLookupReply;

                    if (payload.type === "user_presence_lookup") {
                        handlePresenceReply(payload);
                    }
                } catch (parseError) {
                    log.warn("Failed to parse presence socket payload", parseError);
                }

                return;
            }

            if (frame.command === "ERROR") {
                const message = frame.headers.message || frame.body || "Live presence socket error.";
                setConnectionState("error");
                setError(message);
            }
        };

        const connect = () => {
            if (disposed) {
                return;
            }

            clearTimers();
            closeSocket();
            pendingRequestIdsRef.current.clear();
            bufferRef.current = "";
            setConnectionState("connecting");

            const socket = new WebSocket(getRealtimeSocketUrl());
            socketRef.current = socket;

            socket.onopen = () => {
                if (disposed || socketRef.current !== socket) {
                    return;
                }

                socket.send(
                    buildStompFrame("CONNECT", {
                        "accept-version": "1.2",
                        "heart-beat": `${HEARTBEAT_INTERVAL_MS},${HEARTBEAT_INTERVAL_MS}`,
                        Authorization: `Bearer ${accessToken}`,
                    })
                );
            };

            socket.onmessage = (event) => {
                if (disposed || socketRef.current !== socket || typeof event.data !== "string") {
                    return;
                }

                if (event.data === "\n") {
                    return;
                }

                bufferRef.current += event.data;
                bufferRef.current = bufferRef.current.replace(/^\n+/, "");

                let frameBoundaryIndex = bufferRef.current.indexOf("\0");

                while (frameBoundaryIndex >= 0) {
                    const rawFrame = bufferRef.current.slice(0, frameBoundaryIndex);
                    bufferRef.current = bufferRef.current.slice(frameBoundaryIndex + 1);

                    const parsedFrame = parseStompFrame(rawFrame);

                    if (parsedFrame) {
                        handleFrame(parsedFrame);
                    }

                    frameBoundaryIndex = bufferRef.current.indexOf("\0");
                }
            };

            socket.onerror = () => {
                if (disposed || socketRef.current !== socket) {
                    return;
                }

                log.warn("Live presence socket reported an error.");
            };

            socket.onclose = (event) => {
                if (socketRef.current !== socket) {
                    return;
                }

                socketRef.current = null;

                clearTimers();

                if (disposed) {
                    return;
                }

                log.warn("Live presence socket closed", {
                    code: event.code,
                    reason: event.reason,
                });

                setConnectionState("connecting");

                if (!latestPresenceRef.current) {
                    setError("Live presence disconnected. Reconnecting...");
                }

                scheduleReconnect();
            };
        };

        connect();

        return () => {
            disposed = true;
            clearTimers();
            closeSocket();
            pendingRequestIdsRef.current.clear();
        };
    }, [targetUserId]);

    return {
        presence,
        connectionState,
        error,
        lastUpdatedAt,
        refresh: sendLookup,
    };
};
